"use client";

import { useState } from "react";
import { X, Loader2, ArrowRight, Calendar, FileText, CheckCircle2, ShieldCheck } from "lucide-react";
import { CandidateLead } from "@/lib/demos/hiringDb";

interface CandidateStageModalProps {
  isOpen: boolean;
  onClose: () => void;
  candidate: CandidateLead;
  targetStage: string;
  onConfirm: (updates: Partial<CandidateLead>) => Promise<void>;
}

export function CandidateStageModal({
  isOpen,
  onClose,
  candidate,
  targetStage,
  onConfirm,
}: CandidateStageModalProps) {
  // Interview Round fields
  const [interviewDate, setInterviewDate] = useState(candidate.interview_date || "Tomorrow at 04:00 PM IST");
  const [interviewerName, setInterviewerName] = useState(candidate.interviewer_name || "Board Selection Committee & CEO Panel");
  const [meetingLink, setMeetingLink] = useState(
    candidate.meeting_link || `https://meet.google.com/nex-${candidate.candidate_no.toLowerCase().replace(/[^a-z0-9]/g, "")}`
  );

  // Placed / Won fields
  const [offerCtc, setOfferCtc] = useState(candidate.offer_ctc || candidate.expected_ctc);
  const [invoiceNo, setInvoiceNo] = useState(candidate.invoice_no || `INV-NEXUS-2026-${Math.floor(1000 + Math.random() * 9000)}`);
  const [agreementId, setAgreementId] = useState(candidate.agreement_id || `NEX-ENG-2026-${Math.floor(1000 + Math.random() * 9000)}`);
  
  const feePct = candidate.practice_id === "c-suite" ? 25 : 20;
  const calculatedFee = Math.round((Number(offerCtc) * feePct) / 100);

  const [notes, setNotes] = useState(candidate.notes || "");
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen) return null;

  const isInterviewStage = targetStage === "Client Interview Round";
  const isWonStage = targetStage === "Offer Released & Placed (Won)";
  const sym = candidate.currency === "USD" ? "$" : "₹";

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const updates: Partial<CandidateLead> = {
        stage: targetStage as any,
        notes: notes || undefined,
      };

      if (isInterviewStage) {
        updates.interview_date = interviewDate;
        updates.interviewer_name = interviewerName;
        updates.meeting_link = meetingLink;
      }

      if (isWonStage) {
        updates.offer_ctc = Number(offerCtc);
        updates.invoice_no = invoiceNo;
        updates.agreement_id = agreementId;
        updates.placement_fee = calculatedFee;
      }

      await onConfirm(updates);
      onClose();
    } catch (err) {
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="relative w-full max-w-lg bg-[#140c21] border border-purple-800/50 rounded-2xl shadow-2xl overflow-hidden text-white">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-purple-900/40 bg-[#170f2b]">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center border border-purple-500/30">
              {isInterviewStage ? (
                <Calendar className="w-4 h-4 text-purple-400" />
              ) : isWonStage ? (
                <FileText className="w-4 h-4 text-emerald-400" />
              ) : (
                <CheckCircle2 className="w-4 h-4 text-purple-400" />
              )}
            </div>
            <div>
              <h3 className="font-bold text-white text-sm sm:text-base">
                Advance Candidate Stage
              </h3>
              <p className="text-[11px] text-purple-300">
                Move to: <span className="font-semibold text-white">{targetStage}</span>
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-lg text-slate-400 hover:text-white hover:bg-purple-900/40 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4 text-xs">
          {/* Context Banner */}
          <div className="p-3.5 rounded-xl bg-[#1c1230] border border-purple-900/50 flex items-start gap-3">
            <ShieldCheck className="w-4 h-4 text-purple-400 shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold text-white text-xs">
                {candidate.candidate_name} ({candidate.candidate_no})
              </div>
              <div className="text-[11px] text-slate-400">
                {candidate.target_role} • {candidate.client_company}
              </div>
            </div>
          </div>

          {/* Stage 4: Interview Round Scheduling */}
          {isInterviewStage && (
            <div className="space-y-3.5 pt-1">
              <div className="p-3 rounded-lg bg-purple-500/10 border border-purple-500/20 text-purple-200 text-[11px] leading-relaxed">
                ℹ️ Advancing will automatically generate and attach the <strong>Candidate Evaluation Dossier &amp; Interview Briefing Pass PDF</strong> to the confirmation email.
              </div>

              <div>
                <label className="block text-slate-300 font-medium mb-1">
                  Interview Date &amp; Slot *
                </label>
                <input
                  type="text"
                  required
                  value={interviewDate}
                  onChange={(e) => setInterviewDate(e.target.value)}
                  placeholder="e.g. Tomorrow at 04:00 PM IST"
                  className="w-full bg-[#1c1230] border border-purple-900/60 rounded-xl px-3 py-2 text-white focus:outline-none focus:border-purple-500"
                />
              </div>

              <div>
                <label className="block text-slate-300 font-medium mb-1">
                  Interview Panel / Assessors *
                </label>
                <input
                  type="text"
                  required
                  value={interviewerName}
                  onChange={(e) => setInterviewerName(e.target.value)}
                  placeholder="e.g. Vikramaditya Rao & CEO Board Panel"
                  className="w-full bg-[#1c1230] border border-purple-900/60 rounded-xl px-3 py-2 text-white focus:outline-none focus:border-purple-500"
                />
              </div>

              <div>
                <label className="block text-slate-300 font-medium mb-1">
                  Virtual Meeting Link *
                </label>
                <input
                  type="url"
                  required
                  value={meetingLink}
                  onChange={(e) => setMeetingLink(e.target.value)}
                  placeholder="https://meet.google.com/..."
                  className="w-full bg-[#1c1230] border border-purple-900/60 rounded-xl px-3 py-2 text-white focus:outline-none focus:border-purple-500"
                />
              </div>
            </div>
          )}

          {/* Stage 5: Offer Released & Placed (Won) */}
          {isWonStage && (
            <div className="space-y-3.5 pt-1">
              <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-emerald-200 text-[11px] leading-relaxed">
                🎉 Advancing will execute the placement agreement and attach the <strong>Executive Placement Engagement Agreement &amp; Placement Fee Tax Invoice PDF</strong>.
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-slate-300 font-medium mb-1">
                    Agreed Annual CTC ({sym}) *
                  </label>
                  <input
                    type="number"
                    required
                    value={offerCtc}
                    onChange={(e) => setOfferCtc(Number(e.target.value))}
                    className="w-full bg-[#1c1230] border border-purple-900/60 rounded-xl px-3 py-2 text-white font-mono focus:outline-none focus:border-emerald-500"
                  />
                </div>

                <div>
                  <label className="block text-slate-300 font-medium mb-1">
                    Placement Fee ({feePct}%)
                  </label>
                  <div className="w-full bg-[#120a1d] border border-purple-900/40 rounded-xl px-3 py-2 text-emerald-400 font-mono font-bold">
                    {sym}{calculatedFee.toLocaleString()}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-slate-300 font-medium mb-1">
                    Tax Invoice No. *
                  </label>
                  <input
                    type="text"
                    required
                    value={invoiceNo}
                    onChange={(e) => setInvoiceNo(e.target.value)}
                    className="w-full bg-[#1c1230] border border-purple-900/60 rounded-xl px-3 py-2 text-white font-mono focus:outline-none focus:border-emerald-500"
                  />
                </div>

                <div>
                  <label className="block text-slate-300 font-medium mb-1">
                    Agreement Reference *
                  </label>
                  <input
                    type="text"
                    required
                    value={agreementId}
                    onChange={(e) => setAgreementId(e.target.value)}
                    className="w-full bg-[#1c1230] border border-purple-900/60 rounded-xl px-3 py-2 text-white font-mono focus:outline-none focus:border-emerald-500"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Notes for All Transitions */}
          <div>
            <label className="block text-slate-300 font-medium mb-1">
              Search Partner &amp; Transition Notes
            </label>
            <textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Candidate confirmed slot; panel preparation pack distributed."
              className="w-full bg-[#1c1230] border border-purple-900/60 rounded-xl px-3 py-2 text-white focus:outline-none focus:border-purple-500"
            />
          </div>

          <div className="pt-3 flex justify-end gap-2.5">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-xl bg-purple-950/60 hover:bg-purple-900 text-slate-400 hover:text-white text-xs font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className={`px-5 py-2 rounded-xl text-white font-bold text-xs shadow-lg flex items-center gap-2 transition-all disabled:opacity-50 ${
                isWonStage
                  ? "bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 shadow-emerald-600/30"
                  : "bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 shadow-purple-600/30"
              }`}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Processing &amp; Dispatching...</span>
                </>
              ) : (
                <>
                  <span>Confirm &amp; Dispatch Email</span>
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
